//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';

import { getRgbaForThemeHex } from '@utils/getRgbaForThemeHex';

export interface IButtonStyle {
  color: string;
  backgroundColor: string;
  borderColor: string;
  textDecoration?: string;
}

export interface IButtonStateStyles {
  fixed: {
    fontWeight: 'normal' | 'bold';
    borderShape: 'pill' | 'circle' | 'square' | 'default' | 'none';
    borderThickness: 'thin' | 'thick';
  };
  standard: IButtonStyle;
  hover: IButtonStyle;
  active: IButtonStyle;
  disabled: IButtonStyle;
  isInlineWithText?: boolean;
}

const globalDisabledStyle: IButtonStyle = {
  color: theme.colors.gray4,
  backgroundColor: theme.colors.gray0,
  borderColor: theme.colors.gray0,
};

export const PrimaryButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thick',
  },
  standard: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primary,
    borderColor: theme.colors.aliases.primary,
  },
  hover: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade1,
    borderColor: theme.colors.aliases.primaryShade1,
  },
  active: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade2,
    borderColor: theme.colors.aliases.primaryShade2,
  },
  disabled: globalDisabledStyle,
};

export const SecondaryButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thick',
  },
  standard: {
    color: theme.colors.aliases.primary,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.aliases.primary,
  },
  hover: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade1,
    borderColor: theme.colors.aliases.primaryShade1,
  },
  active: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade2,
    borderColor: theme.colors.aliases.primaryShade2,
  },
  disabled: globalDisabledStyle,
};

export const TertiaryButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thick',
  },
  standard: {
    color: theme.colors.gray8,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.gray8,
  },
  hover: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade1,
    borderColor: theme.colors.aliases.primaryShade1,
  },
  active: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade2,
    borderColor: theme.colors.aliases.primaryShade2,
  },
  disabled: globalDisabledStyle,
};

export const DefaultButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'default',
    borderThickness: 'thin',
  },
  standard: {
    color: theme.colors.aliases.bodyPrimary,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.gray2,
  },
  hover: {
    color: theme.colors.aliases.bodyPrimary,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.aliases.bodyPrimary,
  },
  active: {
    color: theme.colors.aliases.bodyPrimary,
    backgroundColor: theme.colors.gray00,
    borderColor: theme.colors.aliases.bodyPrimary,
  },
  disabled: globalDisabledStyle,
};

export const DefaultPlaceholderButtonStyles: IButtonStateStyles = {
  ...DefaultButtonStyles,
  standard: {
    ...DefaultButtonStyles.standard,
    color: theme.colors.gray8,
  },
};

/**
 * The DefaultError styles are exclusively used in the DropdownButton atom to show an error state
 * for dropdowns used within a form. They are just stored here to help ensure consistency, and
 * should not be used without explicit direction from design.
 */
export const DefaultErrorButtonStyles: IButtonStateStyles = {
  ...DefaultButtonStyles,
  standard: {
    ...DefaultButtonStyles.standard,
    backgroundColor: theme.colors.red0,
    borderColor: theme.colors.aliases.error,
  },
};

/**
 * The DefaultError styles are exclusively used in the DropdownButton atom to show an error state
 * for dropdowns used within a form. They are just stored here to help ensure consistency, and
 * should not be used without explicit direction from design.
 */
export const DefaultErrorPlaceholderButtonStyles: IButtonStateStyles = {
  ...DefaultPlaceholderButtonStyles,
  standard: {
    ...DefaultPlaceholderButtonStyles.standard,
    backgroundColor: theme.colors.red0,
    borderColor: theme.colors.aliases.error,
  },
};

export const DangerButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thick',
  },
  standard: {
    color: theme.colors.red7,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.red7,
  },
  hover: {
    color: theme.colors.white,
    backgroundColor: theme.colors.red7,
    borderColor: theme.colors.red7,
  },
  active: {
    color: theme.colors.white,
    backgroundColor: theme.colors.red8,
    borderColor: theme.colors.red8,
  },
  disabled: globalDisabledStyle,
};

export const FlatPrimaryButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'bold',
    borderShape: 'pill',
    borderThickness: 'thin',
  },
  standard: {
    color: theme.colors.aliases.primary,
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.white,
  },
  hover: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade1,
    borderColor: theme.colors.aliases.primaryShade1,
  },
  active: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade2,
    borderColor: theme.colors.aliases.primaryShade2,
  },
  disabled: globalDisabledStyle,
};

export const FlatDefaultButtonStyles: IButtonStateStyles = {
  ...FlatPrimaryButtonStyles,
  standard: {
    ...FlatPrimaryButtonStyles.standard,
    color: theme.colors.gray9,
  },
};

export const FlatDefaultIconButtonStyles: IButtonStateStyles = {
  ...FlatDefaultButtonStyles,
  fixed: {
    ...FlatDefaultButtonStyles.fixed,
    borderShape: 'circle',
  },
};

export const IconButtonStyles: IButtonStateStyles = {
  ...TertiaryButtonStyles,
  fixed: {
    ...TertiaryButtonStyles.fixed,
    borderShape: 'circle',
  },
};

export const DefaultIconButtonStyles: IButtonStateStyles = {
  ...DefaultButtonStyles,
  fixed: {
    ...DefaultButtonStyles.fixed,
    borderShape: 'square',
  },
};

export const OverlayButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'default',
    borderThickness: 'thin',
  },
  standard: {
    color: theme.colors.white,
    backgroundColor: getRgbaForThemeHex(theme.colors.black, 0.5),
    borderColor: theme.colors.white,
  },
  hover: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade1,
    borderColor: theme.colors.aliases.primaryShade1,
  },
  active: {
    color: theme.colors.white,
    backgroundColor: theme.colors.aliases.primaryShade2,
    borderColor: theme.colors.aliases.primaryShade2,
  },
  disabled: globalDisabledStyle,
};

export const OverlayIconButtonStyles: IButtonStateStyles = {
  ...OverlayButtonStyles,
  fixed: {
    fontWeight: 'bold',
    borderShape: 'circle',
    borderThickness: 'thin',
  },
  standard: {
    ...OverlayButtonStyles.standard,
    borderColor: 'none',
  },
};

export const OverlayIconMediaButtonStyles: IButtonStateStyles = {
  ...OverlayIconButtonStyles,
  standard: {
    ...OverlayIconButtonStyles.standard,
  },
  active: {
    ...OverlayIconButtonStyles.active,
    backgroundColor: getRgbaForThemeHex(theme.colors.black, 0.5),
    borderColor: 'none',
  },
  hover: {
    ...OverlayIconButtonStyles.hover,
    borderColor: 'none',
  },
};

export const PrimaryLinkButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'none',
    borderThickness: 'thin',
  },
  standard: {
    color: theme.colors.aliases.primary,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  hover: {
    color: theme.colors.aliases.primary,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  active: {
    color: theme.colors.aliases.primary,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  disabled: {
    ...globalDisabledStyle,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  isInlineWithText: true,
};

export const BodyPrimaryLinkButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'none',
    borderThickness: 'thin',
  },
  standard: {
    color: theme.colors.aliases.bodyPrimary,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  hover: {
    color: theme.colors.aliases.bodyPrimary,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  active: {
    color: theme.colors.aliases.bodyPrimary,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  disabled: {
    ...globalDisabledStyle,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  isInlineWithText: true,
};

export const ActionSheetButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'none',
    borderThickness: 'thin',
  },
  standard: {
    color: theme.colors.gray9,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  hover: {
    color: theme.colors.gray9,
    backgroundColor: theme.colors.gray0,
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  active: {
    color: theme.colors.gray9,
    backgroundColor: theme.colors.gray0,
    borderColor: 'transparent',
    textDecoration: 'underline',
  },
  disabled: {
    ...globalDisabledStyle,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  isInlineWithText: true,
};

export const InvisibleButtonStyles: IButtonStateStyles = {
  fixed: {
    fontWeight: 'normal',
    borderShape: 'none',
    borderThickness: 'thin',
  },
  standard: {
    color: theme.colors.gray9,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  hover: {
    color: theme.colors.gray9,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  active: {
    color: theme.colors.gray9,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
  disabled: {
    ...globalDisabledStyle,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
  },
};

export const InvisibleButtonDeprecatedStyles: IButtonStateStyles = {
  ...InvisibleButtonStyles,
  standard: {
    ...InvisibleButtonStyles.standard,
    color: theme.colors.gray6,
  },
  hover: {
    ...InvisibleButtonStyles.hover,
    color: theme.colors.gray8,
  },
};
